import {
  createContext,
  type HTMLAttributes,
  type ReactNode,
  useContext,
} from 'react'
import cx from 'classnames'
import { type MotionValue, motionValue, useTransform } from 'motion/react'
import { ScrollShadow } from '@/components/ScrollShadow'
import { clamp } from '@/utils/math-util'
import { useScrollOffsets } from '@/hooks/element-hooks'
import styles from './Card.module.css'

interface CardProps {
  /**
   * The stuff to render in this card
   */
  children?: ReactNode
  /**
   * Optional illustration to render in this card
   */
  illustration?: ReactNode
  /**
   * Opt-out of max-height property
   */
  unconstrained?: boolean

  /**
   * Opt-out of border property
   */
  borderless?: boolean

  /**
   * Optional id
   */
  id?: string
  /**
   * Optional attribute for testing purposes
   */
  testId?: string
}

interface CardScrollContextProps {
  scrollYStart: MotionValue<number>
  scrollYEnd: MotionValue<number>
}

/**
 * When true, descendant cards render as headed sections on the page surface
 * instead of bordered boxes. Titles read `--card-title-size`,
 * `--card-title-weight` and `--card-title-color` so the page sets its own
 * hierarchy.
 */
export const FlatCardsContext = createContext(false)

/**
 * The card's scroll offsets, which fade the header and footer shadows in.
 */
export const CardScrollContext = createContext<CardScrollContextProps>({
  scrollYStart: motionValue(0),
  scrollYEnd: motionValue(0),
})

/**
 * A box that groups related content under a header.
 */
export function Card({
  children,
  illustration,
  testId,
  unconstrained,
  borderless,
  id,
}: CardProps) {
  const { connectRef, scrollYStart, scrollYEnd } = useScrollOffsets()
  const flat = useContext(FlatCardsContext)

  return (
    <div
      className={cx(
        styles.root,
        !borderless && styles.bordered,
        unconstrained && styles.unconstrained,
        flat && styles.flat,
      )}
      data-test={testId}
      id={id}
    >
      <div
        className={styles.cardContentContainer}
        data-slot="card-content-container"
        ref={connectRef}
      >
        <CardScrollContext.Provider value={{ scrollYStart, scrollYEnd }}>
          {children}
        </CardScrollContext.Provider>
      </div>
      {illustration}
    </div>
  )
}

interface CardIllustrationProps {
  src: string
  alt?: string
}

/**
 * An image shown beside the card's content.
 */
export function CardIllustration({ src, alt }: CardIllustrationProps) {
  return (
    <div className={styles.cardIllustration}>
      <img className={styles.cardIllustrationImage} src={src} alt={alt} />
    </div>
  )
}

const DELTA = 24

interface CardHeaderProps {
  children?: ReactNode
  compact?: boolean
  bordered?: boolean
  compactBottomPadding?: boolean
  fullWidth?: boolean
}

/**
 * The top of a card, which casts a shadow once the content scrolls under it.
 */
export function CardHeader({
  children,
  compact,
  bordered,
  compactBottomPadding,
  fullWidth,
}: CardHeaderProps) {
  const { scrollYStart } = useContext(CardScrollContext)
  const shadowOpacity = useTransform(scrollYStart, (v) => clamp(v / DELTA))

  return (
    <div
      className={cx(
        styles.cardHeader,
        compact && styles.headerCompact,
        bordered && styles.headerBordered,
        compactBottomPadding && styles.compactBottomPadding,
        fullWidth && styles.fullWidth,
      )}
      data-slot="card-header"
    >
      {children}
      <ScrollShadow lift borderless opacity={shadowOpacity} />
    </div>
  )
}

interface CardTitleProps {
  children?: ReactNode
  cardLayout?: boolean
  fullWidth?: boolean
  testId?: string
}

/**
 * The card's heading.
 */
export function CardTitle({
  children,
  testId,
  cardLayout,
  fullWidth,
}: CardTitleProps) {
  return (
    <div
      className={cx(
        styles.cardTitle,
        cardLayout && styles.marginTitle,
        fullWidth && styles.fullWidth,
      )}
      data-test={testId}
    >
      {children}
    </div>
  )
}

interface CardTitleCountProps {
  children?: ReactNode
}

/**
 * A count shown after the card's title.
 */
export function CardTitleCount({ children }: CardTitleCountProps) {
  return <span className={styles.cardTitleCount}>{children}</span>
}

interface CardHeaderActionsProps {
  children?: ReactNode
}

/**
 * Actions aligned to the end of the card's header.
 */
export function CardHeaderActions({ children }: CardHeaderActionsProps) {
  return <div className={styles.cardHeaderActions}>{children}</div>
}

interface CardTabsProps {
  children?: ReactNode
  bordered?: boolean
}

/**
 * A tab bar below the card's header.
 */
export function CardTabs({ children, bordered }: CardTabsProps) {
  return (
    <div className={cx(styles.cardTabs, bordered && styles.bordered)}>
      {children}
    </div>
  )
}

interface CardContentProps {
  children?: ReactNode
  compact?: boolean
  listContent?: boolean
  compactBottomPadding?: boolean
  autoHeight?: boolean
  scroll?: boolean
}

/**
 * The card's body.
 */
export function CardContent({
  children,
  compact,
  listContent,
  compactBottomPadding,
  autoHeight,
  scroll,
}: CardContentProps) {
  return (
    <div
      className={cx(
        styles.cardContent,
        compact && styles.contentCompact,
        listContent && styles.listContent,
        compactBottomPadding && styles.cardContentCompactPaddingBottom,
        autoHeight && styles.cardContentAutoHeight,
        scroll && styles.scroll,
      )}
      data-slot="card-content"
    >
      {children}
    </div>
  )
}

interface CardContentDescriptionProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode
}

/**
 * Supporting text in the card's body.
 */
export function CardContentDescription({
  children,
  ...rest
}: CardContentDescriptionProps) {
  return (
    <div className={styles.cardContentDescription} {...rest}>
      {children}
    </div>
  )
}

interface CardFooterProps {
  children?: ReactNode
}

/**
 * The bottom of a card, which casts a shadow while content remains below it.
 */
export function CardFooter({ children }: CardFooterProps) {
  const { scrollYEnd } = useContext(CardScrollContext)
  const shadowOpacity = useTransform(scrollYEnd, (v) => clamp(v / DELTA - 1))

  return (
    <div className={styles.cardFooter}>
      <ScrollShadow placement="top" lift borderless opacity={shadowOpacity} />
      {children}
    </div>
  )
}

interface CardFooterContentProps {
  children?: ReactNode
}

/**
 * The footer's main content.
 */
export function CardFooterContent({ children }: CardFooterContentProps) {
  return <div className={styles.cardFooterContent}>{children}</div>
}

interface CardFooterDescriptionProps {
  children?: ReactNode
}

/**
 * Supporting text in the card's footer.
 */
export function CardFooterDescription({
  children,
}: CardFooterDescriptionProps) {
  return <div className={styles.cardFooterDescription}>{children}</div>
}
